import { getApolloErrorMessage } from '@utils/getApolloErrorMessage';
import { isConnectionFailure } from '@utils/graphqlError';

import type { ApolloError } from '@apollo/client';
import type { TFunction } from 'i18next';

/**
 * User-facing copy for a create/update form submit that failed.
 *
 * A dropped connection (ERR_INTERNET_DISCONNECTED, DNS/TLS failure) carries
 * no GraphQL body, so `getApolloErrorMessage` can only return the raw fetch
 * message ("Failed to fetch" / "Network request failed") — show the shared
 * connection copy for those instead. Falls back to `shared:exceptionMsg`
 * rather than an empty string, so a dialog is never blank.
 */
// Only an Apollo error carries a message meant for the user. Anything else
// reaching here is a JS-level failure, whose `message` would put text like
// "Cannot read property 'id' of undefined" in front of the user.
const isApolloError = (error: unknown): error is ApolloError =>
  !!error &&
  typeof error === 'object' &&
  ('graphQLErrors' in error || 'networkError' in error);

export const getSubmitErrorMessage = (error: unknown, t: TFunction): string => {
  if (!isApolloError(error)) return t('shared:exceptionMsg');
  return isConnectionFailure(error)
    ? t('shared:connectionIssueMsg')
    : (getApolloErrorMessage(error) ?? t('shared:exceptionMsg'));
};
